import { lazy } from "react";
import { Route, Navigate } from "react-router-dom";
import { SegmentGuard } from "@/components/auth/SegmentGuard";

// Core pages
const SirppiLandingPage = lazy(() => import("@/pages/SirppiLandingPage").then(m => ({ default: m.SirppiLandingPage })));
const LandingPage = lazy(() => import("@/pages/LandingPage").then(m => ({ default: m.LandingPage })));
const ExplorePage = lazy(() => import("@/pages/ExplorePage").then(m => ({ default: m.ExplorePage })));
const RolePage = lazy(() => import("@/pages/RolePage").then(m => ({ default: m.RolePage })));
const PublicCareerProfile = lazy(() => import("@/pages/PublicCareerProfile").then(m => ({ default: m.PublicCareerProfile })));
const SectionPage = lazy(() => import("@/pages/SectionPage").then(m => ({ default: m.SectionPage })));
const RoleQuizPage = lazy(() => import("@/pages/RoleQuizPage").then(m => ({ default: m.RoleQuizPage })));
const QuizResultsPage = lazy(() => import("@/pages/QuizResultsPage"));
const GuidedPathsPage = lazy(() => import("@/pages/GuidedPathsPage").then(m => ({ default: m.GuidedPathsPage })));
const CompareRolesPage = lazy(() => import("@/pages/CompareRolesPage").then(m => ({ default: m.CompareRolesPage })));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const CareerHubPage = lazy(() => import("@/pages/CareerHubPage").then(m => ({ default: m.default })));
const CareerHubSettingsPage = lazy(() => import("@/pages/CareerHubSettingsPage").then(m => ({ default: m.default })));
const SkillProfilePage = lazy(() => import("@/pages/SkillProfilePage"));
const PassportPublicPage = lazy(() => import("@/pages/PassportPublicPage"));

// School segment pages
const SchoolStreamGuidancePage = lazy(() => import("@/pages/school/SchoolStreamGuidancePage"));
const SchoolCollegeFinderPage = lazy(() => import("@/pages/school/SchoolCollegeFinderPage"));
const SchoolExamPrepPage = lazy(() => import("@/pages/school/SchoolExamPrepPage"));
const SchoolAdmissionsPage = lazy(() => import("@/pages/school/SchoolAdmissionsPage"));
const SchoolParentSharePage = lazy(() => import("@/pages/school/SchoolParentSharePage"));

// College segment pages
const CollegeInternshipsPage = lazy(() => import("@/pages/college/CollegeInternshipsPage"));
const CollegeEventsPage = lazy(() => import("@/pages/college/CollegeEventsPage"));
const CollegePlacementPrepPage = lazy(() => import("@/pages/college/CollegePlacementPrepPage"));
const CollegeHigherEducationPage = lazy(() => import("@/pages/college/CollegeHigherEducationPage"));

const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const ReferralsDashboardPage = lazy(() => import("@/pages/ReferralsDashboardPage"));
const WelcomeGatePage = lazy(() => import("@/pages/WelcomeGatePage"));

const ProductsPage = lazy(() => import("@/pages/ProductsPage"));
const CompanyPage = lazy(() => import("@/pages/CompanyPage"));
const ConnectMcpPage = lazy(() => import("@/pages/ConnectMcpPage"));

// Newsletter
const NewsletterVerifyPage = lazy(() => import("@/pages/NewsletterVerifyPage"));
const NewsletterUnsubscribePage = lazy(() => import("@/pages/NewsletterUnsubscribePage"));
const NewsletterPreferencesPage = lazy(() => import("@/pages/NewsletterPreferencesPage"));

// Government careers
const GovernmentCareerDetailPage = lazy(() => import("@/pages/GovernmentCareerDetailPage"));
const GovernmentCompareRolesPage = lazy(() => import("@/pages/GovernmentCompareRolesPage"));
const GovernmentCareersArchivePage = lazy(() => import("@/pages/GovernmentCareersArchivePage"));
const SavedGovernmentJobsPage = lazy(() => import("@/pages/SavedGovernmentJobsPage"));

// Static pages
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const RefundPage = lazy(() => import("@/pages/RefundPage"));
const CancellationPage = lazy(() => import("@/pages/CancellationPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const DesignSystemPage = lazy(() => import("@/pages/DesignSystemPage").then(m => ({ default: m.DesignSystemPage })));
const OfflinePage = lazy(() => import("@/pages/OfflinePage"));
const InstallPage = lazy(() => import("@/pages/InstallPage"));

// Beta & misc
const BetaLandingPage = lazy(() => import("@/pages/BetaLandingPage"));
const BetaCodeOnboardingPage = lazy(() => import("@/pages/BetaCodeOnboardingPage"));
const BetaTesterDashboard = lazy(() => import("@/pages/BetaTesterDashboard"));

export const publicRoutes = (
  <>
    {/* Marketing landing page */}
    <Route path="/" element={<SirppiLandingPage />} />
    <Route path="/career-path" element={<LandingPage />} />
    {/* Legacy slug — keep permanently so old links/emails/QRs continue to work */}
    <Route path="/compass" element={<Navigate to="/career-path" replace />} />
    
    {/* Free exploration */}
    <Route path="/explore" element={<ExplorePage />} />
    <Route path="/quiz" element={<RoleQuizPage />} />
    <Route path="/quiz-results" element={<QuizResultsPage />} />
    
    {/* Newsletter */}
    <Route path="/newsletter/verify" element={<NewsletterVerifyPage />} />
    <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
    <Route path="/newsletter/preferences" element={<NewsletterPreferencesPage />} />
    
    {/* Products & Company */}
    <Route path="/products" element={<ProductsPage />} />
    <Route path="/company" element={<CompanyPage />} />
    <Route path="/connect" element={<ConnectMcpPage />} />
    <Route path="/mcp" element={<ConnectMcpPage />} />
    
    {/* Government Careers */}
    <Route path="/government-careers" element={<Navigate to="/explore?type=government" replace />} />
    <Route path="/government-careers/archive" element={<GovernmentCareersArchivePage />} />
    <Route path="/government-careers/:slug" element={<GovernmentCareerDetailPage />} />
    <Route path="/government/compare" element={<GovernmentCompareRolesPage />} />
    
    {/* Shareable public career profile */}
    <Route path="/career-profile/:profileId" element={<PublicCareerProfile />} />
    
    {/* Skill DNA + Passport */}
    <Route path="/skill-profile" element={<SkillProfilePage />} />
    <Route path="/passport/:token" element={<PassportPublicPage />} />

    {/* User pages */}
    <Route path="/profile" element={<ProfilePage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/referrals" element={<ReferralsDashboardPage />} />
    <Route path="/saved" element={<Navigate to="/profile" replace />} />
    <Route path="/guided-paths" element={<GuidedPathsPage />} />
    <Route path="/compare" element={<CompareRolesPage />} />
    <Route path="/career-hub" element={<CareerHubPage />} />
    <Route path="/career-hub/settings" element={<CareerHubSettingsPage />} />
    <Route path="/career-hub/stream-guidance" element={<SegmentGuard allow={["school_student"]}><SchoolStreamGuidancePage /></SegmentGuard>} />
    <Route path="/career-hub/colleges" element={<SegmentGuard allow={["school_student"]}><SchoolCollegeFinderPage /></SegmentGuard>} />
    <Route path="/career-hub/exam-prep" element={<SegmentGuard allow={["school_student"]}><SchoolExamPrepPage /></SegmentGuard>} />
    <Route path="/career-hub/admissions" element={<SegmentGuard allow={["school_student"]}><SchoolAdmissionsPage /></SegmentGuard>} />
    <Route path="/career-hub/parent-share" element={<SegmentGuard allow={["school_student"]}><SchoolParentSharePage /></SegmentGuard>} />
    <Route path="/career-hub/internships" element={<SegmentGuard allow={["college_student"]}><CollegeInternshipsPage /></SegmentGuard>} />
    <Route path="/career-hub/events" element={<SegmentGuard allow={["college_student"]}><CollegeEventsPage /></SegmentGuard>} />
    <Route path="/career-hub/placement-prep" element={<SegmentGuard allow={["college_student"]}><CollegePlacementPrepPage /></SegmentGuard>} />
    <Route path="/career-hub/higher-education" element={<SegmentGuard allow={["college_student"]}><CollegeHigherEducationPage /></SegmentGuard>} />
    <Route path="/hub" element={<Navigate to="/career-hub" replace />} />
    {/* Legacy /insights entry — Insights is a section inside Career Hub, not a standalone route */}
    <Route path="/insights" element={<Navigate to="/career-hub?tab=insights" replace />} />
    <Route path="/insights/*" element={<Navigate to="/career-hub?tab=insights" replace />} />
    {/* Catch-all: any unknown /career-hub/* path falls back to the segment-aware hub home */}
    <Route path="/career-hub/*" element={<Navigate to="/career-hub" replace />} />
    <Route path="/section/:sectionId" element={<SectionPage />} />
    <Route path="/role/:sectionId/:roleId" element={<RolePage />} />
    <Route path="/settings" element={<SettingsPage />} />
    <Route path="/welcome/:featureName" element={<WelcomeGatePage />} />
    
    {/* Static pages */}
    <Route path="/terms" element={<TermsPage />} />
    <Route path="/privacy" element={<PrivacyPage />} />
    <Route path="/refund" element={<RefundPage />} />
    <Route path="/cancellation" element={<CancellationPage />} />
    <Route path="/pricing" element={<PricingPage />} />
    <Route path="/contact" element={<ContactPage />} />
    <Route path="/about" element={<AboutPage />} />
    <Route path="/design-system" element={<DesignSystemPage />} />
    <Route path="/offline" element={<OfflinePage />} />
    <Route path="/install" element={<InstallPage />} />
    
    {/* Beta */}
    <Route path="/beta" element={<BetaLandingPage />} />
    <Route path="/beta/code" element={<BetaCodeOnboardingPage />} />
    <Route path="/beta/dashboard" element={<BetaTesterDashboard />} />
  </>
);
